import { useEffect, useRef } from 'react';
import { useReadingSession } from './ReadingSession';
import VoiceHalo from './VoiceHalo';
import { fitsInView, glideIntoView } from '../tarot-utils/glide';

/**
 * The voice circle as a spread shows it, above its cards. Renders nothing
 * unless a narrated reading is under way.
 *
 * When the reading starts it brings itself and the cards below it into view,
 * clear of the dock, instead of the page jumping down to the text.
 */
function ReadingHalo() {
  const session = useReadingSession();
  const ref = useRef(null);
  const shown = Boolean(session?.active && session.narrated);

  useEffect(() => {
    const el = ref.current;
    if (!shown || !el) return undefined;

    // After the halo's own entrance and the dock have laid out. The dock
    // covers the bottom of the screen, so the cards must end above it.
    const timer = setTimeout(() => {
      const reserve = (document.querySelector('.narration-dock')?.offsetHeight || 160) + 12;
      const halo = el.getBoundingClientRect();
      const cards = el.nextElementSibling?.getBoundingClientRect() || halo;
      // Circle and cards together if they fit; otherwise the cards win and
      // the dock's small circle stands in for this one
      if (fitsInView(cards.bottom - halo.top, reserve)) {
        glideIntoView(halo.top, cards.bottom, { reserve });
      } else {
        glideIntoView(cards.top, cards.bottom, { reserve });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [shown]);

  // Gone from the page counts as out of sight, so the dock shows its own
  const setHaloVisible = session?.setHaloVisible;
  useEffect(() => {
    if (!shown) setHaloVisible?.(false);
  }, [shown, setHaloVisible]);

  if (!shown) return null;

  const { narrator, status } = session;
  const done = narrator.ended && status === 'done';

  return (
    <div ref={ref} className="reading-halo">
      <VoiceHalo
        analyser={narrator.analyser}
        active={!done}
        playing={narrator.speaking}
        onVisibleChange={setHaloVisible}
      />
    </div>
  );
}

export default ReadingHalo;
